/**
 * useTeamChannels Hook
 *
 * Fetches channels for a Microsoft Team in a managed tenant.
 *
 * Uses an in-memory cache to prevent refetching when navigating between pages.
 */

import { useState, useEffect, useCallback } from 'react';
import { TenantApiService } from '../services/tenantApi';
import type { TeamChannel } from '../types/sharepoint';

interface UseTeamChannelsOptions {
  tenantId: string;
  teamId: string;
  enabled?: boolean;
}

interface UseTeamChannelsResult {
  channels: TeamChannel[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface CacheEntry {
  channels: TeamChannel[];
  timestamp: number;
}

// Module-level cache to persist across component mounts
const teamChannelsCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Invalidate the channels cache for a specific team.
 * Call this after creating a new channel to force refetch.
 */
export function invalidateTeamChannelsCache(tenantId: string, teamId: string): void {
  const cacheKey = `channels:${tenantId}:${teamId}`;
  teamChannelsCache.delete(cacheKey);
}

/**
 * Hook to fetch channels for a Microsoft Team.
 *
 * @param options.tenantId - The managed tenant ID
 * @param options.teamId - The Microsoft Team ID
 * @param options.enabled - Whether to fetch channels (default: true)
 * @returns Object with channels, loading state, error, and refetch function
 */
export function useTeamChannels({
  tenantId,
  teamId,
  enabled = true,
}: UseTeamChannelsOptions): UseTeamChannelsResult {
  // Create cache key
  const cacheKey = `channels:${tenantId}:${teamId}`;
  const cachedEntry = tenantId && teamId ? teamChannelsCache.get(cacheKey) : undefined;
  const isCacheValid = cachedEntry && (Date.now() - cachedEntry.timestamp) < CACHE_TTL_MS;

  const [channels, setChannels] = useState<TeamChannel[]>(isCacheValid ? cachedEntry.channels : []);
  const [isLoading, setIsLoading] = useState<boolean>(!isCacheValid && enabled && !!tenantId && !!teamId);
  const [error, setError] = useState<string | null>(null);

  const fetchChannels = useCallback(async (skipCache = false) => {
    // Don't fetch if disabled or missing required IDs
    if (!enabled || !tenantId || !teamId) {
      setChannels([]);
      setIsLoading(false);
      return;
    }

    // Check cache first (unless skipCache is true for manual refresh)
    if (!skipCache) {
      const cached = teamChannelsCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
        setChannels(cached.channels);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedChannels = await TenantApiService.getTeamChannels(tenantId, teamId);
      setChannels(fetchedChannels);
      // Update cache
      teamChannelsCache.set(cacheKey, { channels: fetchedChannels, timestamp: Date.now() });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch channels';
      setError(errorMessage);
      setChannels([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, teamId, enabled, cacheKey]);

  // Fetch channels on mount and when dependencies change
  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // Manual refetch bypasses cache
  const refetch = useCallback(async () => {
    await fetchChannels(true);
  }, [fetchChannels]);

  return {
    channels,
    isLoading,
    error,
    refetch,
  };
}
